import { useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ExternalLink, Globe, Highlighter } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';

import { AppShell } from '@/components/AppShell';
import { HighlightList } from '@/components/HighlightList';
import { SelectionHighlighter } from '@/components/SelectionHighlighter';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useHighlightsOfSource } from '@/hooks/useHighlights';
import { markify, normalizePhrases } from '@/lib/markify';
import { urlMatchCandidates } from '@/lib/normalizeUrl';
import { extractArticle } from '@/lib/readability';
import { sanitizeUrl } from '@/lib/sanitizeUrl';

/** In-app reader for web pages — extract, read, select-to-highlight. */
export function ReaderPage() {
  const params = useParams<{ url: string }>();
  const url = sanitizeUrl(params.url ?? '');
  const queryClient = useQueryClient();
  const articleRef = useRef<HTMLElement>(null);

  const article = useQuery({
    queryKey: ['reader', url ?? ''],
    queryFn: () => extractArticle(url!),
    enabled: !!url,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  // Highlights of this page (raw + canonical URL forms).
  const candidates = useMemo(() => (url ? urlMatchCandidates(url) : []), [url]);
  const { highlights, isPending: highlightsPending } = useHighlightsOfSource({ r: candidates.length > 0 ? candidates : undefined });

  const phrases = useMemo(() => normalizePhrases(highlights.map((h) => h.quote)), [highlights]);

  const markdownComponents = useMemo<Components>(
    () => ({
      p: ({ node, children, ...props }) => <p {...props}>{markify(children, phrases)}</p>,
      li: ({ node, children, ...props }) => <li {...props}>{markify(children, phrases)}</li>,
      h1: ({ node, children, ...props }) => <h1 {...props}>{markify(children, phrases)}</h1>,
      h2: ({ node, children, ...props }) => <h2 {...props}>{markify(children, phrases)}</h2>,
      h3: ({ node, children, ...props }) => <h3 {...props}>{markify(children, phrases)}</h3>,
      h4: ({ node, children, ...props }) => <h4 {...props}>{markify(children, phrases)}</h4>,
      blockquote: ({ node, children, ...props }) => <blockquote {...props}>{markify(children, phrases)}</blockquote>,
      td: ({ node, children, ...props }) => <td {...props}>{markify(children, phrases)}</td>,
      th: ({ node, children, ...props }) => <th {...props}>{markify(children, phrases)}</th>,
    }),
    [phrases],
  );

  useSeoMeta({
    title: article.data ? `${article.data.title} — glowmark reader` : 'Reader — glowmark',
    description: article.data?.excerpt || 'Read and highlight this page on glowmark.',
  });

  // Optimistically add freshly published highlights to the rail + marks.
  const handlePublished = (published: NostrEvent) => {
    queryClient.setQueryData<NostrEvent[]>(['nostr', 'highlights-of', '', '', ...candidates], (old) => [
      published,
      ...(old ?? []),
    ]);
  };

  const highlightsSection = (
    <section id="highlights" className="space-y-4">
      <h2 className="flex items-center gap-2 text-lg font-semibold">
        <Highlighter className="size-4.5 text-primary" />
        Highlights
        {highlights.length > 0 && (
          <Badge variant="secondary" className="ml-1">{highlights.length}</Badge>
        )}
      </h2>
      {highlightsPending ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full rounded-lg" />
          <Skeleton className="h-20 w-full rounded-lg" />
        </div>
      ) : highlights.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-8 px-6 text-center">
            <p className="text-sm text-muted-foreground max-w-xs mx-auto">
              No highlights of this page yet. Select any passage while reading to mark the first.
            </p>
          </CardContent>
        </Card>
      ) : (
        <HighlightList highlights={highlights} />
      )}
    </section>
  );

  if (!url) {
    return (
      <AppShell>
        <div className="container max-w-2xl py-16">
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="text-muted-foreground max-w-sm mx-auto">That doesn't look like a valid URL.</p>
            </CardContent>
          </Card>
        </div>
      </AppShell>
    );
  }

  const hostname = new URL(url).hostname;

  return (
    <AppShell>
      <div className="container max-w-5xl py-8 sm:py-10">
        {article.isPending ? (
          <div className="max-w-2xl mx-auto space-y-6">
            <Skeleton className="h-10 w-3/4" />
            <Skeleton className="h-4 w-40" />
            <div className="space-y-3 pt-4">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-4/5" />
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-3/5" />
            </div>
          </div>
        ) : !article.data ? (
          <div className="max-w-2xl mx-auto">
            <Card className="border-dashed">
              <CardContent className="py-12 px-8 text-center space-y-4">
                <Globe className="size-8 text-primary mx-auto" />
                <p className="text-muted-foreground max-w-sm mx-auto">
                  Couldn't extract a readable article from this page. It may require JavaScript,
                  block proxies, or simply not be article-shaped.
                </p>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline underline-offset-4"
                >
                  <ExternalLink className="size-4" />
                  Open the original page
                </a>
              </CardContent>
            </Card>
            <div className="mt-10">{highlightsSection}</div>
          </div>
        ) : (
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
            {/* Article */}
            <article ref={articleRef} className="min-w-0">
              <header className="space-y-3">
                <h1 className="font-serif text-3xl sm:text-4xl font-medium leading-tight tracking-tight">
                  {article.data.title}
                </h1>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <Globe className="size-3.5" />
                    {article.data.siteName || hostname}
                  </span>
                  {article.data.byline && <span>· {article.data.byline}</span>}
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 hover:text-foreground hover:underline underline-offset-4"
                  >
                    <ExternalLink className="size-3.5" />
                    Original
                  </a>
                </div>
              </header>

              <div className="article-body mt-8">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {article.data.markdown}
                </ReactMarkdown>
              </div>

              {/* Highlights below the article on small screens */}
              <div className="mt-12 lg:hidden">{highlightsSection}</div>
            </article>

            {/* Sticky highlights rail on large screens */}
            <aside className="hidden lg:block">
              <div className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto pr-1 pb-4">
                {highlightsSection}
              </div>
            </aside>
          </div>
        )}

        {article.data && (
          <SelectionHighlighter
            containerRef={articleRef}
            sourceContent={article.data.markdown}
            source={{ type: 'web', url }}
            onPublished={handlePublished}
          />
        )}
      </div>
    </AppShell>
  );
}

export default ReaderPage;
